import {
  ApolloLink,
  NextLink,
  Operation,
  Observable,
  FetchResult,
} from "@apollo/client";



const generateUUID = (): string => {
  if (typeof crypto !== "undefined" && crypto.randomUUID)
    return crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

export function createCommonHeadersMiddleware(): ApolloLink {
  return new ApolloLink((operation: Operation, forward: NextLink) => {
    if (typeof window === "undefined") {
      // SSR-safe: just forward the operation without browser-only logic
      return forward(operation);
    }

    // Skip middleware for internal product/platform fetches to prevent infinite loop
    const context = operation.getContext();
    if (context.skipCommonHeaders) {
      return forward(operation);
    }

    return new Observable<FetchResult>((observer) => {
      const execute = async () => {
        try {
          const existingHeaders = operation.getContext().headers || {};

          const version = process.env.NEXT_PUBLIC_CLIENT_VERSION || "1.0.0.1";

          const osName = getOSName();
          const osVersion = getOSVersion();
          const browser = getBrowserName();
          const browserVersion = getBrowserVersion();
          const deviceId = getOrCreateDeviceId();
          const locale = getClientLocale();
          const timestamp = Date.now().toString();
          const traceId = generateUUID();
          const accessToken = localStorage.getItem("token");


          // Step 4: Combine headers
          const commonHeaders = {
            Authorization: accessToken ? `Bearer ${accessToken}` : "",
            "X-Client-Platform": "WEB",
            "X-Client-Product": "NCMS1.0",
            "X-Client-Version": version,
            "X-OS-Name": osName,
            "X-OS-Version": osVersion,
            "X-Browser": browser,
            "X-Browser-Version": browserVersion,
            "X-Device-Id": deviceId,
            "X-Client-Locale": locale,
            "X-Request-Timestamp": timestamp,
            "X-Request-Signature": generateRequestSignature(
              operation,
              timestamp
            ),
            "X-Trace-Id": traceId,
          };

          //  Step 5: Set headers safely
          operation.setContext({
            headers: {
              ...commonHeaders,
              ...existingHeaders, // allow overrides
            },
          });

          // Step 6: Forward the operation
          const sub = forward(operation).subscribe({
            next: observer.next.bind(observer),
            error: observer.error.bind(observer),
            complete: observer.complete.bind(observer),
          });

          return () => sub.unsubscribe();
        } catch {
          // Prevent client-side errors leaking to UI
          observer.complete();
        }
      };

      execute();
    });
  });
}

/* ============================================================================
 *  Helper Utilities
 * ========================================================================== */
const safeUserAgent = (): string =>
  typeof navigator !== "undefined" ? navigator.userAgent : "";

const getOSName = (): string => {
  const ua = safeUserAgent();
  if (/Windows/i.test(ua)) return "WINDOWS";
  if (/Mac OS/i.test(ua)) return "MACOS";
  if (/Android/i.test(ua)) return "ANDROID";
  if (/iPhone|iPad|iPod/i.test(ua)) return "IOS";
  if (/Linux/i.test(ua)) return "LINUX";
  return "UNKNOWN";
};

const getOSVersion = (): string => {
  const match = safeUserAgent().match(
    /(Windows NT|Android|iPhone OS|Mac OS X) ([\d_\.]+)/
  );
  return match ? match[2].replace(/_/g, ".") : "unknown";
};

const getBrowserName = (): string => {
  const ua = safeUserAgent();
  if (ua.includes("Edg")) return "EDGE";
  if (ua.includes("Chrome")) return "CHROME";
  if (ua.includes("Safari")) return "SAFARI";
  if (ua.includes("Firefox")) return "FIREFOX";
  if (ua.includes("OPR") || ua.includes("Opera")) return "OPERA";
  return "UNKNOWN";
};

const getBrowserVersion = (): string => {
  const match = safeUserAgent().match(
    /(Chrome|Firefox|Safari|Edg|OPR)\/(\d+(\.\d+)?)/
  );
  return match ? match[2] : "unknown";
};

const getOrCreateDeviceId = (): string => {
  if (typeof localStorage === "undefined") return "server-device";
  let deviceId = localStorage.getItem("device_id");
  if (!deviceId) {
    deviceId = generateUUID();
    localStorage.setItem("device_id", deviceId);
  }
  return deviceId;
};

const getClientLocale = (): string => {
  return typeof navigator !== "undefined" && navigator.language
    ? navigator.language
    : "en-IN";
};

const generateRequestSignature = (
  operation: Operation,
  timestamp: string
): string => {
  return "HMAC-SHA256=disabled";
};
